import { _ as _extends } from '../../_rollupPluginBabelHelpers-mO86zOh3.js';
import { forwardRef, useEffect } from 'react';
import useId from '@indeed/ihl-use-id';
import { useDisclosurePopupContext } from './context.js';
import { useMergeRefs } from '../../hooks/useMergeRefs.js';
import { jsx } from '@emotion/react';
import { Popup } from '../Popup/Popup.js';
import { Portal } from '../Portal/Portal.js';
import '@floating-ui/react-dom';
import '@indeed/ifl-css';
import '../Box/Box.js';
import 'react-dom';
import '../IndeedThemeProvider/IndeedThemeProvider.js';
import '@indeed/ifl-icons';
import '../IndeedThemeProvider/utils.js';
import '../IndeedThemeProvider/theme.js';
import 'deepmerge';
import '@indeed/ifl-themes';
import '../LinkProvider/LinkProvider.js';

// Content box
const DisclosurePopupContent = /*#__PURE__*/forwardRef((props, forwardedRef) => {
  const {
    alignment = "start",
    children,
    closeOnClick = false,
    id,
    side = "block-end",
    sx,
    tabIndex,
    ...remainingProps
  } = props;
  const {
    anchor,
    contentRef,
    isOpen,
    portaledPopup,
    setContentId,
    setOpen,
    triggerRef
  } = useDisclosurePopupContext();
  const contentId = useId(id);
  const mergedContentRef = useMergeRefs(forwardedRef, contentRef);
  const handleOnBlur = event => {
    if (isOpen && !(contentRef.current?.contains(event.relatedTarget) || triggerRef.current?.contains(event.relatedTarget))) {
      setOpen(false);
      if (portaledPopup) {
        /**
         * Focus is being sent back to the trigger element, but for some reason
         * the focus indicator was not being applied. `requestAnimationFrame`
         * addressed this.
         */
        requestAnimationFrame(() => {
          triggerRef.current?.focus();
        });
      }
    }
    remainingProps.onBlur?.(event);
  };
  const handleOnKeyDown = event => {
    if (isOpen && event.key === "Escape") {
      event.stopPropagation();
      setOpen(false);
      triggerRef.current?.focus();
    }
    remainingProps.onKeyDown?.(event);
  };
  const handleOnClick = event => {
    // Check if target is contentRef so clicking on the padding doesn't close the popup
    if (closeOnClick && event.target !== contentRef.current) {
      setOpen(false);
      triggerRef.current?.focus();
    }
    remainingProps.onClick?.(event);
  };
  useEffect(() => {
    setContentId(contentId);
  }, [contentId, setContentId]);
  const DisclosurePopupContentPopup = jsx(Popup, _extends({
    ref: mergedContentRef,
    alignment: alignment,
    anchor: anchor,
    side: side,
    sx: {
      display: isOpen ? "block" : "none",
      ":focus-visible": {
        outline: "none",
        boxShadow: "focus"
      },
      ":focus": {
        boxShadow: "medium"
      },
      maxInlineSize: "400px",
      minInlineSize: "200px",
      padding: 3,
      overflowX: "hidden",
      overflowY: "auto",
      zIndex: 1,
      ...sx
    }
  }, remainingProps, {
    id: contentId,
    tabIndex: tabIndex ?? -1,
    onBlur: handleOnBlur,
    onKeyDown: handleOnKeyDown,
    onClick: handleOnClick
  }), children);
  return portaledPopup ? jsx(Portal, null, DisclosurePopupContentPopup) : DisclosurePopupContentPopup;
});
DisclosurePopupContent.displayName = "DisclosurePopupContent";

export { DisclosurePopupContent };
